import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [messagesByChannel, setMessagesByChannel] = useState({});
  const [activeChannel, setActiveChannel] = useState(null);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const messageTypes = {
    TEXT: 'text',
    IMAGE: 'image',
    FILE: 'file',
    SYSTEM: 'system',
    ANNOUNCEMENT: 'announcement'
  };

  const channelTypes = {
    COURSE: 'course',
    DIRECT: 'direct',
    GROUP: 'group',
    ANNOUNCEMENT: 'announcement'
  };

  // Auto-scroll vers le bas quand les messages changent
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesByChannel]);

  // Charger les canaux quand l'utilisateur est authentifié
  useEffect(() => {
    if (user) {
      loadChannels();
    } else {
      setChannels([]);
      setMessagesByChannel({});
      setActiveChannel(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const normalizeMessage = (msg, channelId) => {
    const fullName = [msg.user?.first_name, msg.user?.last_name].filter(Boolean).join(' ') || 'Inconnu';
    const initials = fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    return {
      id: msg.id,
      channelId,
      content: msg.message,
      author: {
        id: msg.user_id === user?.id ? 'current_user' : msg.user_id,
        name: fullName,
        role: msg.user?.role || 'student',
        avatar: initials,
      },
      timestamp: msg.created_at,
    };
  };

  const loadMessages = async (channelId) => {
    try {
      const { data } = await axios.get(`/api/chat/course/${channelId}`);
      if (data.success) {
        const mapped = (data.messages || []).map(msg => normalizeMessage(msg, channelId));
        setMessagesByChannel(prev => ({ ...prev, [channelId]: mapped }));
      }
    } catch (err) {
      console.error('[ChatContext] Erreur chargement messages:', err);
    }
  };

  const loadChannels = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/analytics/my-progress');
      if (data.success) {
        const enrollments = data.enrollments || [];
        const mapped = enrollments
          .filter(e => e.course)
          .map(e => ({
            id: e.course.id,
            name: e.course.title,
            type: channelTypes.COURSE,
            courseId: e.course.id,
            unreadCount: 0,
            isActive: true,
          }));
        setChannels(mapped);
        if (mapped.length > 0) {
          setActiveChannel(mapped[0]);
          await loadMessages(mapped[0].id);
        }
      }
    } catch (err) {
      console.error('[ChatContext] Erreur chargement canaux:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (channelId, content) => {
    try {
      const { data } = await axios.post(`/api/chat/course/${channelId}`, { message: content });
      if (data.success) {
        const newMsg = normalizeMessage(data.message, channelId);
        setMessagesByChannel(prev => ({
          ...prev,
          [channelId]: [...(prev[channelId] || []), newMsg],
        }));
        setChannels(prev => prev.map(c =>
          c.id === channelId
            ? { ...c, lastMessage: { content, timestamp: newMsg.timestamp, author: 'Vous' } }
            : c
        ));
        return { success: true, message: newMsg };
      }
    } catch (err) {
      console.error('[ChatContext] Erreur envoi message:', err);
      return { success: false, error: err.message };
    }
  };

  const switchChannel = async (channelId) => {
    const channel = channels.find(c => c.id === channelId);
    if (!channel) return;
    setActiveChannel(channel);
    setChannels(prev => prev.map(c => c.id === channelId ? { ...c, unreadCount: 0 } : c));
    if (!messagesByChannel[channelId]) {
      await loadMessages(channelId);
    }
  };

  const getChannelMessages = (channelId) => messagesByChannel[channelId] || [];

  const getTotalUnreadCount = () => channels.reduce((total, c) => total + c.unreadCount, 0);

  const searchMessages = (query, channelId = null) => {
    const msgs = channelId
      ? (messagesByChannel[channelId] || [])
      : Object.values(messagesByChannel).flat();
    return msgs.filter(m => m.content.toLowerCase().includes(query.toLowerCase()));
  };

  const value = {
    messages: Object.values(messagesByChannel).flat(),
    channels,
    activeChannel,
    onlineUsers: [],
    isTyping: {},
    loading,
    messageTypes,
    channelTypes,
    messagesEndRef,
    sendMessage,
    switchChannel,
    getChannelMessages,
    createChannel: async () => ({ success: false, error: 'Non supporté' }),
    getTotalUnreadCount,
    simulateTyping: () => {},
    getChannelOnlineUsers: () => [],
    searchMessages,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
